// Appointments (design/screens/appointments.html; UC-APPOINTMENTS-007, 009, 010, 011, 002 A1, 004): the day or the week by staff
// column over the synced records (`GET /appointments/calendar`), the same header figures as Today (a week's are its six days'
// added up), the detail panel with the status actions the machine allows (confirm, arrive, start, complete, cancel with a
// reason, no-show) and Edit — the notes and the staff member, the two fields of the prototype's booking panel Vision X keeps
// (UC-APPOINTMENTS-011) — "Book in Zoho Bookings" in place of the prototype's booking form (D53), Walk-in (UC-APPOINTMENTS-008),
// Recalls (UC-APPOINTMENTS-013), Sync now with the line that follows a running pull to its end, the unmatched-bookings inbox,
// and the appointment's reminder rows read-only in the detail panel (UC-APPOINTMENTS-014 step 5; sending and re-send are
// Sprint 6). Date, view, branch and the inbox flag live in the URL. The panel's Consent line names a patient without a current
// consent, with the desk capture and the kiosk, and Arrived asks for it (UC-GOVERNANCE-017 A1, BR-GOVERNANCE-027).
import { type AppointmentPatch, type AppointmentReminderView, type AppointmentStatus, type AppointmentView, type BookingsSyncResponse, type BookingsSyncRunView, type TodayFigures, type UserRef } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { appointments, bookings, type TransitionSegment } from "../api/endpoints.appointments";
import { describeError, isApiError } from "../api/problem";
import { DOT, addDays, formatDate, formatDateTime, formatTime, isLocalDate, localDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Chip, ErrorAlert, Field, KV, Loading, Modal, Panel, Select, TextArea, type Tone } from "../ui/bits";
import { Icon } from "../ui/icons";
import { ApptStatusChip, ApptWarnings, STACKED_QUERY, STATUS_WORD, TRANSITION_OF, TYPE_TONE, bookingsRefText, minutesOfDay, patientSub, recallNotice, showAnswer, typeClass } from "./AppointmentsBits";
import { DayGrid, WeekGrid, localDateOfInstant, onGrid, setCalendarTimezone } from "./AppointmentsCalendar";
import { ConsentActions, ConsentChip, consentArrivalNotice, consentToAsk } from "./AppointmentsConsent";
import { InboxPanel } from "./AppointmentsInbox";
import { WalkInButton } from "./AppointmentsWalkIn";
import { NoAccessPanel, NoActiveBranch, PageHead } from "./common";

/** Monday of the week holding `date` (a `YYYY-MM-DD`), as a calendar date. */
export function mondayOf(date: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 Sunday
  return addDays(date, dow === 0 ? -6 : 1 - dow);
}
const weekdayShort = (date: string): string => new Intl.DateTimeFormat("en-ZA", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${date}T00:00:00.000Z`));
const longDate = (date: string): string => new Intl.DateTimeFormat("en-ZA", { weekday: "long", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00.000Z`));

/**
 * A range's figures (BR-APPOINTMENTS-011): each day's, as the calendar read carries them — counted by the same definitions as
 * Today's — added up field by field. The days partition the range, so the sum is the range's own count.
 */
function sumFigures(days: readonly { figures: TodayFigures }[]): TodayFigures {
  const total: TodayFigures = { booked: 0, arrived: 0, completed: 0, inConsultation: 0, waiting: 0, toCome: 0, noShows: 0, cancelled: 0 };
  for (const d of days) for (const k of Object.keys(total) as (keyof TodayFigures)[]) total[k] += d.figures[k];
  return total;
}

/** The Booking panel's line is read again every minute, like the calendar, and every four seconds while the pull it names runs. */
const RUN_REFRESH_MS = 60_000;
const RUN_FOLLOW_MS = 4_000;
/** A run `Running` this long is dead (BR-APPOINTMENTS-008): the next scheduled pull closes it, and the minute's read shows that. */
const RUN_DEAD_MS = 10 * 60_000;

/** Whether the line follows `run` closely (UC-APPOINTMENTS-002 A1 sub-step 4): it is running, and not for so long that it is dead. */
const followsRun = (run: BookingsSyncRunView | null, nowMs: number): boolean => run?.outcome === "Running" && nowMs - Date.parse(run.startedAt) < RUN_DEAD_MS;

/** Whether run `a` began after run `b`, in the history's order: start time, then id. */
function startedAfter(a: BookingsSyncRunView, b: BookingsSyncRunView): boolean {
  const ta = Date.parse(a.startedAt);
  const tb = Date.parse(b.startedAt);
  return ta !== tb ? ta > tb : a.id > b.id;
}

/**
 * The run the Booking panel's line names (UC-APPOINTMENTS-002 A1 sub-step 4): the branch's newest from the history, or the run
 * Sync now answered while the history has not caught up with it. Once the history holds that run finished, or a newer run, the
 * history's wins — an answer never holds the line on a state that has moved on; a run only ever goes from running to finished.
 */
function shownRun(answered: BookingsSyncRunView | null, newest: BookingsSyncRunView | null): BookingsSyncRunView | null {
  if (!answered || !newest) return answered ?? newest;
  if (newest.id === answered.id) return newest.outcome === "Running" && answered.outcome !== "Running" ? answered : newest;
  return startedAfter(newest, answered) ? newest : answered;
}

export function Appointments() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const [params, setParams] = useSearchParams();
  const todayDate = localDate(new Date());
  const dateParam = params.get("date");
  const date = isLocalDate(dateParam) ? dateParam : todayDate;
  const view = params.get("view") === "week" ? "week" : "day";
  const branchParam = params.get("branchId");
  const branchId = branchParam && s?.switchableBranches.some((b) => b.id === branchParam) ? branchParam : (s?.activeBranchId ?? undefined);
  const inboxFlag = params.get("inbox") === "1";
  const from = view === "week" ? mondayOf(date) : date;
  const to = view === "week" ? addDays(from, 5) : date;
  const days = view === "week" ? [0, 1, 2, 3, 4, 5].map((n) => addDays(from, n)) : [date];
  const branchName = s?.switchableBranches.find((b) => b.id === branchId)?.name ?? s?.assignments.find((a) => a.branchId === branchId)?.branchName ?? "";
  useEffect(() => { if (s) setCalendarTimezone(s.settings.timezone); }, [s]);

  const cal = useQuery({ queryKey: ["calendar", branchId ?? "", from, to], queryFn: () => appointments.calendar({ branchId, from, to }), enabled: !!branchId, refetchInterval: 60_000 });
  // Today's read is the Day view's header; a week counts from the calendar's own per-day figures, so it is not asked for then.
  const figures = useQuery({ queryKey: ["today", branchId ?? "", date], queryFn: () => appointments.today({ branchId, date }), enabled: !!branchId && view === "day", refetchInterval: 60_000 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Below 1000 px the side column sits under the calendar (UC-APPOINTMENTS-007 A6), so a chosen block's panel is brought into
  // view; beside the calendar it is already in sight and the page stays where it is.
  const side = useRef<HTMLDivElement>(null);
  const [bringIntoView, setBringIntoView] = useState(0);
  const choose = (a: AppointmentView) => {
    setSelectedId(a.id);
    setEditingId(null);
    if (typeof window.matchMedia === "function" && window.matchMedia(STACKED_QUERY).matches) setBringIntoView((n) => n + 1);
  };
  useEffect(() => { if (bringIntoView > 0) side.current?.firstElementChild?.scrollIntoView?.({ block: "nearest" }); }, [bringIntoView]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [cancelling, setCancelling] = useState<AppointmentView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<BookingsSyncResponse | null>(null);
  // The run Sync now answered is shown for its own branch only: another branch shows its own last run (review S7).
  const answered = lastRun && lastRun.branchId === branchId ? lastRun : null;
  const runs = useQuery({
    queryKey: ["sync-runs", branchId ?? ""], queryFn: () => bookings.syncRuns({ branchId, limit: 1 }), enabled: !!branchId,
    // UC-APPOINTMENTS-002 A1 sub-step 4: read again every four seconds while the pull the line names is running, else every
    // minute. A read made offline waits for the connection (the queries' default network mode), so no tick asks until it is back.
    refetchInterval: (q) => (followsRun(shownRun(answered, q.state.data?.data.items[0] ?? null), Date.now()) ? RUN_FOLLOW_MS : RUN_REFRESH_MS),
  });
  const data = cal.data?.data ?? null;
  // The header's figures (BR-APPOINTMENTS-011): the day's from Today's read; the week's from the calendar's, over the six days shown.
  const f = view === "week" ? (data ? sumFigures(data.days) : undefined) : figures.data?.data.figures;
  const selected = data?.appointments.find((a) => a.id === selectedId) ?? null;
  /** Where the kiosk comes back to after a consent handover: this calendar, as it is. */
  const here = `/appointments${params.toString() ? `?${params.toString()}` : ""}`;
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v === null) next.delete(k); else next.set(k, v); }
    setParams(next, { replace: true });
  };
  const nowMinutes = date === todayDate ? minutesOfDay(new Date().toISOString()) : null;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["calendar"] }); void qc.invalidateQueries({ queryKey: ["today"] }); };
  const typeCounts = (data?.types ?? []).map((t) => ({ ...t, n: (data?.appointments ?? []).filter((a) => a.appointmentTypeId === t.id && onGrid(a)).length })).filter((t) => t.n > 0);
  const offGrid = (data?.appointments ?? []).filter((a) => !onGrid(a)).length;

  const act = async (a: AppointmentView, segment: TransitionSegment | "cancel", reason?: string) => {
    setBusy(a.id);
    setNotice(null);
    try {
      const { data: r } = segment === "cancel" ? await appointments.cancel(a.id, reason ?? "") : await appointments.transition(a.id, segment);
      // UC-GOVERNANCE-017 A1: the check-in went ahead; a patient without a current consent is asked for it now.
      const ask = segment === "arrive" ? consentArrivalNotice(r, here, setNotice) : null;
      // UC-APPOINTMENTS-010 step 2: a completion says the recall due date it set, after any warning.
      const recall = recallNotice(r);
      // The panel shows the answer's status and actions at once; the re-read below brings the figures (UC-APPOINTMENTS-009 step 3).
      showAnswer(qc, r);
      const warned = r.warnings.length > 0 ? <Alert tone={r.warnings.some((w) => w.code === "patient_dormant") ? "warn" : "crit"}><b>{a.patient.displayName}:</b> {r.warnings.map((w) => w.message).join(" ")}</Alert> : null;
      if (warned || ask || recall) setNotice(<>{warned}{ask}{recall}</>);
      else if (!r.changed) setNotice(<Alert tone="info">Already {STATUS_WORD[r.status].toLowerCase()}.</Alert>);
      setCancelling(null);
      refresh();
    } catch (err) {
      if (isApiError(err) && err.code === "ILLEGAL_TRANSITION") {
        const allowed = Array.isArray(err.meta.allowed) ? err.meta.allowed.map((x) => STATUS_WORD[x as AppointmentStatus] ?? String(x)) : [];
        setNotice(<Alert tone="warn"><b>Not allowed from {STATUS_WORD[String(err.meta.from ?? a.status) as AppointmentStatus] ?? String(err.meta.from)}.</b> {allowed.length > 0 ? `Possible now: ${allowed.join(", ")}.` : "Nothing more can be done with this appointment."}</Alert>);
        refresh();
      } else setNotice(<Alert tone="crit"><b>Could not update {a.patient.displayName}.</b> {describeError(err)}</Alert>);
    } finally {
      setBusy(null);
    }
  };
  const syncNow = async () => {
    if (!branchId) return;
    setBusy("sync");
    setNotice(null);
    try {
      const { data: run } = await bookings.sync({ branchId: branchId as never });
      setLastRun(run);
      refresh();
      void qc.invalidateQueries({ queryKey: ["inbox"] });
      void qc.invalidateQueries({ queryKey: ["sync-runs"] });
    } catch (err) {
      if (isApiError(err) && err.code === "PROVIDER_NOT_READY") setNotice(<Alert tone="warn"><b>Zoho Bookings is not connected yet.</b> {Array.isArray(err.meta.missing) && err.meta.missing.length > 0 ? `Missing: ${err.meta.missing.join(", ")}.` : ""} The platform operator sets the Zoho secrets (D35).</Alert>);
      else setNotice(<Alert tone="crit"><b>Sync failed.</b> {describeError(err)}</Alert>);
    } finally {
      setBusy(null);
    }
  };
  const run = shownRun(answered, runs.data?.data.items[0] ?? null);
  // The pull the line was following has ended: what it changed is read at once — the calendar, the figures and the inbox —
  // rather than at the next minute's read (UC-APPOINTMENTS-002 A1 sub-step 4). Another branch's run is not this one's end, and
  // an end Sync now answered itself was read with its answer.
  const followedBranch = useRef<string | null>(null);
  useEffect(() => {
    if (run?.outcome === "Running") { followedBranch.current = run.branchId; return; }
    const was = followedBranch.current;
    followedBranch.current = null;
    if (run && run.branchId === was && run !== answered) { refresh(); void qc.invalidateQueries({ queryKey: ["inbox"] }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id, run?.outcome]);
  // The branch's Zoho Bookings booking page as an Admin typed it on the mapping (D63); a branch with none offers no Bookings link
  // (UC-APPOINTMENTS-007 A3) — never the generic Zoho app — and the Booking panel says who sets it.
  const bookingsHref = data?.bookingsUrl ?? null;
  // Who a walk-in or an edit may name: every Active user assigned to the branch; an older answer without it falls back to the columns.
  const assignable = data && data.assignableStaff.length > 0 ? data.assignableStaff : (data?.staff ?? []);

  return (
    <section className="screen" id="s-appointments">
      <div className="content">
        <PageHead
          title="Appointments"
          sub={`${view === "week" ? `Week of ${formatDate(`${from}T00:00:00.000Z`)}` : longDate(date)}${DOT}${branchName}${f ? `${DOT}${f.booked} booked${DOT}${f.arrived} arrived${DOT}${f.noShows} no-show${DOT}${f.toCome} still to come` : ""}`}
          actions={<>
            <button className="btn sm prev" type="button" onClick={() => set({ date: addDays(date, view === "week" ? -7 : -1) })}><Icon name="chevron" />Previous</button>
            <button className="btn sm" type="button" onClick={() => set({ date: null })}>Today</button>
            <button className="btn sm next" type="button" onClick={() => set({ date: addDays(date, view === "week" ? 7 : 1) })}>Next<Icon name="chevron" /></button>
            <span className="seg" role="group" aria-label="View">
              <button type="button" className={view === "day" ? "on" : ""} aria-pressed={view === "day"} onClick={() => set({ view: null })}>Day</button>
              <button type="button" className={view === "week" ? "on" : ""} aria-pressed={view === "week"} onClick={() => set({ view: "week" })}>Week</button>
            </span>
            {can(s, "appointments.recall.read") && <Link className="btn" to="/appointments/recalls">Recalls</Link>}
            <WalkInButton branchId={branchId} staff={assignable} onDone={(n) => setNotice(n)} />
            {bookingsHref && <a className="btn primary" href={bookingsHref} target="_blank" rel="noopener noreferrer"><Icon name="plus" />Book in Zoho Bookings</a>}
          </>}
        />
        <div className="toolbar">
          {s && s.canSwitchBranch ? (
            <div className="input sel w-branch"><select aria-label="Branch" value={branchId ?? ""} onChange={(e) => set({ branchId: e.target.value === s.activeBranchId ? null : e.target.value })}>{s.switchableBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
          ) : <div className="input ro w-branch">{branchName}</div>}
          <div className="chips">
            {typeCounts.map((t) => <Chip key={t.id} tone={TYPE_TONE[typeClass(t.code)]} dot>{t.name} · {t.n}</Chip>)}
            {offGrid > 0 && <Chip tone="neutral">{offGrid} cancelled or moved</Chip>}
          </div>
          <span className="right small faint">{nowMinutes !== null ? `Now ${formatTime(new Date())} · ` : ""}bookings are made in Zoho Bookings and appear here within five minutes</span>
        </div>
        {notice}

        <div className="split">
          <div className="stack gap-16">
            {!branchId && <NoActiveBranch what="its calendar" />}
            {cal.isPending && cal.fetchStatus === "fetching" && <Loading what="calendar" />}
            {/* Offline, a day or week never read is not asked for until the connection is back (UC-APPOINTMENTS-007 A7). */}
            {cal.isPending && cal.fetchStatus === "paused" && <Alert tone="warn"><b>This {view === "week" ? "week" : "day"} isn't available offline.</b> Only days opened while online are kept on this device; it loads once the connection is back.</Alert>}
            {cal.isError && (isApiError(cal.error) && cal.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Appointments" error={cal.error} /> : <ErrorAlert error={cal.error} />)}
            {data && (view === "week"
              ? <WeekGrid view={data} days={days} selectedId={selectedId} onSelect={choose} dayLabel={weekdayShort} />
              : <DayGrid view={data} selectedId={selectedId} onSelect={choose} nowMinutes={nowMinutes} />)}
          </div>

          <div className="stack gap-16" ref={side}>
            {selected && editingId === selected.id ? (
              <EditPanel a={selected} staff={assignable} blockTitle={block ? WRITE_BLOCK_TITLE[block] : undefined} onCancel={() => setEditingId(null)} onSaved={(v) => {
                setEditingId(null);
                setNotice(<Alert tone="ok"><b>Saved {v.patient.displayName}'s appointment.</b> The notes and staff member stay in Vision X; Zoho Bookings keeps its own.</Alert>);
                refresh();
              }} />
            ) : selected ? (
              <Panel title={selected.patient.displayName} meta={<span className="mono">{patientSub(selected)}</span>} head={<span className="right row c gap-4">
                {can(s, "appointments.appointment.write") && <button className="btn sm" type="button" disabled={!online} title={block ? WRITE_BLOCK_TITLE[block] : "Edit the notes and the staff member"} onClick={() => { setNotice(null); setEditingId(selected.id); }}>Edit</button>}
                <button className="btn sm ghost" type="button" onClick={() => { setSelectedId(null); setEditingId(null); }}>Close</button>
              </span>}
                foot={can(s, "appointments.appointment.write") ? (
                  <div className="row c gap-8 wrap">
                    {(["Confirmed", "Arrived", "InProgress", "Completed", "NoShow"] as const).filter((to) => selected.allowedTransitions.includes(to)).map((to) => (
                      <button key={to} className={`btn sm${to === "Arrived" || to === "InProgress" || to === "Completed" ? " primary" : ""}`} type="button" disabled={!online || busy === selected.id} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void act(selected, TRANSITION_OF[to].segment as TransitionSegment)}>{TRANSITION_OF[to].label}</button>
                    ))}
                    {selected.allowedTransitions.includes("Cancelled") && <button className="btn sm danger" type="button" disabled={!online || busy === selected.id} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setCancelling(selected)}>Cancel…</button>}
                    {selected.allowedTransitions.length === 0 && <span className="help">{STATUS_WORD[selected.status]} — nothing more to do here.</span>}
                    <RebookLink a={selected} />
                  </div>
                ) : rebookable(selected) ? <div className="row c gap-8"><RebookLink a={selected} /></div> : undefined}>
                <ApptWarnings warnings={selected.warnings} />
                <KV rows={[
                  ["When", `${formatDateTime(selected.startAt)} – ${formatTime(selected.endAt)} · ${selected.durationMinutes} min`],
                  ["Type", <>{selected.appointmentType.name}{selected.typeUnmapped && <span className="small faint"> · service not mapped</span>}</>],
                  ["Seen by", selected.optometrist?.displayName ?? <span className="faint">Unassigned{selected.staffUnmapped ? " · staff member not mapped" : ""}</span>],
                  ["Status", <><ApptStatusChip status={selected.status} />{selected.awaitingApproval && <span className="small faint"> · awaiting approval in Bookings</span>}</>],
                  ["Patient", <><Link to={`/patients/${selected.patientId}`}>Open record</Link> · {selected.patientStatus}</>],
                  ...(consentToAsk(selected) ? [["Consent", <span className="row c gap-8 wrap"><ConsentChip a={selected} /><ConsentActions a={selected} returnTo={here} onError={setNotice} /></span>] as [ReactNode, ReactNode]] : []),
                  ...(selected.notes ? [["Notes", selected.notes] as [ReactNode, ReactNode]] : []),
                  ...(selected.cancellationReason ? [["Cancelled", `${formatDateTime(selected.cancelledAt)} · ${selected.cancellationReason}`] as [ReactNode, ReactNode]] : []),
                  ["Source", selected.source === "bookings" ? <>Zoho Bookings{selected.bookings ? ` · ${bookingsRefText(selected.bookings)}` : null}{selected.bookings?.summaryUrl && <> · <a href={selected.bookings.summaryUrl} target="_blank" rel="noopener noreferrer">Open in Bookings</a></>}</> : "Walk-in"],
                ]} />
                <Reminders a={selected} />
              </Panel>
            ) : (
              <Panel title="Booking" meta="Zoho Bookings owns the slot">
                <p className="muted">Book, move or cancel a slot in Zoho Bookings; it appears here on the next pull. Choose an appointment on the calendar for its status actions.</p>
                {data && !bookingsHref && <span className="help">This branch has no Zoho Bookings booking page yet — an Admin maps the workspace and adds its booking page in Settings › Appointments.</span>}
                <div className="row c gap-8">
                  {bookingsHref && <a className="btn" href={bookingsHref} target="_blank" rel="noopener noreferrer">Book in Zoho Bookings</a>}
                  {can(s, "appointments.appointment.write") && <button className="btn" type="button" disabled={!online || busy === "sync" || !branchId} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void syncNow()}>{busy === "sync" ? "Syncing…" : "Sync now"}</button>}
                </div>
                {/* Nothing until the history has answered — never "No pull has run" while it is read; the counts are the pull's own, the
                    inbox panel's are the whole inbox (UC-APPOINTMENTS-002 A1 sub-step 4). */}
                {(run || runs.isSuccess) && <span className="help">{run ? `Last pull ${run.finishedAt ? formatDateTime(run.finishedAt) : "running"} · ${run.outcome}${run.outcome === "Succeeded" ? ` · ${run.counts.fetched} fetched · ${run.counts.created} new · ${run.counts.updated} updated · ${run.counts.inbox} unmatched in this pull` : run.errorText ? ` · ${run.errorText}` : ""}` : "No pull has run for this branch yet."}</span>}
              </Panel>
            )}
            <InboxPanel branchId={branchId} highlight={inboxFlag} />
          </div>
        </div>
        {view === "day" && <span className="help">Blocks run from the start time in half-hour rows; {localDateOfInstant(new Date().toISOString()) === date ? "this is today" : `this is ${weekdayShort(date)}, not today`}.</span>}
      </div>
      {cancelling && <CancelModal a={cancelling} busy={busy === cancelling.id} onClose={() => setCancelling(null)} onCancel={(reason) => void act(cancelling, "cancel", reason)} />}
    </section>
  );
}

function CancelModal({ a, busy, onClose, onCancel }: { a: AppointmentView; busy: boolean; onClose: () => void; onCancel: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  const submit = (e: FormEvent) => { e.preventDefault(); if (reason.trim()) onCancel(reason.trim()); };
  return (
    <Modal title={`Cancel ${a.patient.displayName}'s appointment`} meta={`${formatDateTime(a.startAt)} · ${a.appointmentType.name}`} onClose={onClose} onSubmit={submit} footer={<>
      <button className="btn" type="button" onClick={onClose}>Keep it</button>
      <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Cancelling…" : "Cancel appointment"}</button>
    </>}>
      <Field label="Reason" required htmlFor="cancel-reason" help={a.source === "bookings" ? "Recorded on the appointment and pushed back to Zoho Bookings." : "Recorded on the appointment. A walk-in is not in Zoho Bookings, so nothing is sent there."}><TextArea id="cancel-reason" value={reason} maxLength={500} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="Patient phoned — unwell" /></Field>
    </Modal>
  );
}

const REMINDER_TONE: Record<AppointmentReminderView["status"], Tone> = { Pending: "neutral", Sent: "ok", Suppressed: "warn", Failed: "crit", Cancelled: "neutral" };
/** Why a reminder will not go (UC-APPOINTMENTS-014 E1–E4, A2, A4), in the front desk's words: NoChannel is theirs to phone. */
export const SUPPRESSION_WORD: Record<NonNullable<AppointmentReminderView["suppressionReason"]>, string> = {
  OptedOut: "the patient opted out", ConsentMissing: "no reminder consent", PatientDormant: "the patient is dormant", PatientArchived: "the patient is archived",
  NoChannel: "no email on file — phone the patient", ConsolidatedIntoOther: "sent with another appointment's reminder", TypeDisabled: "reminders are switched off", AppointmentChanged: "the appointment changed",
};
/**
 * The appointment's reminder rows (UC-APPOINTMENTS-014 step 5): when each is due, how and to whom, and its status with the reason
 * one is suppressed or cancelled. Read only — sending and the manual re-send are Sprint 6.
 */
function Reminders({ a }: { a: AppointmentView }) {
  const q = useQuery({ queryKey: ["appointment", a.id, "reminders"], queryFn: () => appointments.reminders(a.id) });
  const rows = q.data?.data.items ?? [];
  return (
    <div className="stack gap-4" aria-label="Reminders" role="group">
      <div className="small strong muted">Reminders</div>
      {q.isPending && <Loading what="reminders" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {q.data && rows.length === 0 && <span className="help">None — a walk-in, or an appointment booked less than 24 hours ahead, gets no reminder.</span>}
      {rows.length > 0 && (
        <table>
          <thead><tr><th>Kind</th><th>Due</th><th>Channel</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.kind}</td>
                <td className="nw">{formatDateTime(r.scheduledFor)}</td>
                <td>{r.channel ?? <span className="faint">—</span>}{r.recipientValueNormalised && <div className="small faint">{r.recipientValueNormalised}</div>}</td>
                <td><Chip tone={REMINDER_TONE[r.status]}>{r.status}</Chip>{r.suppressionReason && <div className="small faint">{SUPPRESSION_WORD[r.suppressionReason]}</div>}{r.sentAt && <div className="small faint">sent {formatDateTime(r.sentAt)}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** A no-show or a cancellation is booked again in Zoho Bookings, on the branch's booking page when the answer carries it (D63). */
const rebookable = (a: AppointmentView): boolean => (a.status === "NoShow" || a.status === "Cancelled") && !!a.bookingsUrl;
function RebookLink({ a }: { a: AppointmentView }) {
  if (!rebookable(a)) return null;
  return <a className="btn sm" href={a.bookingsUrl!} target="_blank" rel="noopener noreferrer">Rebook</a>;
}

/** The statuses in which the staff member may still change (UC-APPOINTMENTS-011 A1, E3). */
const STAFF_EDITABLE: readonly AppointmentStatus[] = ["Scheduled", "Confirmed", "Arrived"];

/**
 * Edit (UC-APPOINTMENTS-011): the notes — on any appointment (A2) — and the staff member while it is Scheduled, Confirmed or
 * Arrived, from everyone active at the branch (`assignableStaff`, which includes cover staff with no column). Only what changed is sent (`PATCH /appointments/{id}`); appointments carry no
 * version, so no If-Match. Nothing here goes to Zoho Bookings. Esc cancels.
 */
function EditPanel({ a, staff, blockTitle, onCancel, onSaved }: { a: AppointmentView; staff: readonly UserRef[]; blockTitle: string | undefined; onCancel: () => void; onSaved: (v: AppointmentView) => void }) {
  const [notes, setNotes] = useState(a.notes ?? "");
  const [staffId, setStaffId] = useState<string>(a.optometristUserId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const staffEditable = STAFF_EDITABLE.includes(a.status);
  const options = a.optometrist && !staff.some((u) => u.id === a.optometrist!.id) ? [...staff, a.optometrist] : staff;
  const body: AppointmentPatch = {};
  const nextNotes = notes.trim() ? notes.trim() : null;
  if (nextNotes !== (a.notes ?? null)) body.notes = nextNotes;
  if (staffEditable && (staffId || null) !== (a.optometristUserId ?? null)) body.optometristId = (staffId || null) as never;
  const dirty = Object.keys(body).length > 0;
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const shown = ["optometristId", "notes"];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty || busy || blockTitle) return;
    setBusy(true);
    setError(null);
    try {
      const { data: v } = await appointments.patch(a.id, body);
      onSaved(v);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => void submit(e)} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); onCancel(); } }} aria-label={`Edit ${a.patient.displayName}'s appointment`}>
      <Panel title={`Edit ${a.patient.displayName}`} meta={`${formatTime(a.startAt)} · ${a.appointmentType.name} · ${STATUS_WORD[a.status]}`}
        foot={<>
          <span className="help">Times, type and patient are changed in Zoho Bookings.</span>
          <button className="btn right" type="button" onClick={onCancel}>Cancel</button>
          <button className="btn primary" type="submit" disabled={!dirty || busy || !!blockTitle} title={blockTitle}>{busy ? "Saving…" : "Save changes"}</button>
        </>}>
        {error !== null && (isApiError(error) && error.code === "IMMUTABLE_RECORD"
          ? <Alert tone="warn"><b>The staff member can no longer change.</b> The consultation has started or the appointment is closed; the notes can still be edited.</Alert>
          : !(isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 && error.errors.every((f) => shown.includes(f.path))) && <Alert tone="crit"><b>Could not save.</b> {describeError(error)}</Alert>)}
        <Field label="Seen by" htmlFor="appt-staff" error={fieldErr("optometristId")} help={staffEditable ? "Who will see the patient: anyone active at this branch, or Unassigned." : `Fixed once the appointment is ${STATUS_WORD[a.status].toLowerCase()}.`}>
          <Select id="appt-staff" value={staffId} disabled={!staffEditable} onChange={(e) => setStaffId(e.target.value)}>
            <option value="">Unassigned</option>
            {options.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
          </Select>
        </Field>
        <Field label="Notes" htmlFor="appt-notes" error={fieldErr("notes")} help="Up to 2 000 characters; not sent to Zoho Bookings.">
          <TextArea id="appt-notes" value={notes} rows={3} maxLength={2000} autoFocus onChange={(e) => setNotes(e.target.value)} placeholder="Bring current spectacles" />
        </Field>
      </Panel>
    </form>
  );
}
